import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { View } from 'dashboard/components/PlanUpgradeFlow';
import Tooltip from 'dashboard/components/Tooltip';
import { TextWithDashedUnderline } from 'dashboard/components/Typography';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  AvailableOrganizationPlan,
  OrganizationPlan,
  organizationPlanIconsMap,
  OrganizationPlanKey,
} from 'dashboard/models/organization-plan';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';

interface ComparePlansProps {
  currentOrganizationPlan: OrganizationPlan;
  newOrganizationPlan: AvailableOrganizationPlan | null;
  requiredProductFeatures: ProductFeatureName[];
  loadingAvailablePlans: boolean;
  handleLoadAvailablePlans: (
    requiredProductFeatures: ProductFeatureName[],
  ) => Promise<AvailableOrganizationPlan[]>;
  setNewOrganizationPlan: (newOrganizationPlan: AvailableOrganizationPlan) => void;
  handleNavToNextView: (
    currentView: View,
    newOrganizationPlan: AvailableOrganizationPlan | null,
  ) => void;
  handleNavToPreviousView: (currentView: View) => void;
  onClose: () => void;
}

// These are the only OrganizationPlans that can currently show up as an upgrade candidate
// in a comparison against the current plan. If the newOrganizationPlan is outside of these plans,
// then we just show a generic screen about requesting access instead of a comparison
// against the current plan.
const SUPPORTED_PLANS_FOR_COMPARISON = [
  OrganizationPlanKey.Starter,
  OrganizationPlanKey.StarterPenguin,
  OrganizationPlanKey.Essential,
  OrganizationPlanKey.Growth,
];

const ComparePlans = ({
  currentOrganizationPlan,
  newOrganizationPlan,
  requiredProductFeatures,
  loadingAvailablePlans,
  handleLoadAvailablePlans,
  setNewOrganizationPlan,
  handleNavToNextView,
  handleNavToPreviousView,
  onClose,
}: ComparePlansProps) => {
  useEffect(() => {
    const fetchAvailablePlans = async () => {
      const availablePlans = await handleLoadAvailablePlans(requiredProductFeatures);
      if (availablePlans.length > 0) {
        setNewOrganizationPlan(availablePlans[0]);
      }
    };

    fetchAvailablePlans();
  }, [requiredProductFeatures, handleLoadAvailablePlans]);

  if (loadingAvailablePlans) {
    return (
      <StyledModalContainer>
        <LoadingIndicator />
      </StyledModalContainer>
    );
  }

  if (!newOrganizationPlan) {
    return (
      <StyledModalContainer>
        <ModalHeader
          prefixIcon={<ArrowLeftIcon size={20} />}
          onPrefixIconClick={() => handleNavToPreviousView(View.ComparePlans)}
          onClose={onClose}
        >
          Request access
        </ModalHeader>
        <ModalBody>
          <Paragraph size='lg' color='primary'>
            Sorry, something went wrong. Please try again later.
          </Paragraph>
        </ModalBody>
      </StyledModalContainer>
    );
  }

  if (SUPPORTED_PLANS_FOR_COMPARISON.includes(newOrganizationPlan.key)) {
    return (
      <StyledModalContainer>
        <ModalHeader
          prefixIcon={<ArrowLeftIcon size={20} />}
          onPrefixIconClick={() => handleNavToPreviousView(View.ComparePlans)}
          onClose={onClose}
        >
          Upgrade to {newOrganizationPlan['display-name']}
        </ModalHeader>
        <ModalBody>
          <Paragraph size='lg' color='primary'>
            This feature is not available on your current plan. The{' '}
            {newOrganizationPlan['display-name']} plan offers this feature and could fit your needs.
          </Paragraph>
          <Spacer height={2} />
          <PlanCardsWrapper>
            <PlanCard>
              <div>
                <Sticker variant='basic'>Current</Sticker>
              </div>
              <Spacer height={2} />
              <PlanCardIcon>
                {organizationPlanIconsMap[currentOrganizationPlan.attributes['display-icon-key']]}
              </PlanCardIcon>
              <Spacer height={2} />
              <PlanCardTitle>{currentOrganizationPlan.attributes['display-name']}</PlanCardTitle>
              {planMetadata[currentOrganizationPlan.attributes.key]?.subtitle && (
                <>
                  <PlanCardSubtitle>
                    {planMetadata[currentOrganizationPlan.attributes.key]?.subtitle}
                  </PlanCardSubtitle>
                  <Spacer height={3} />
                </>
              )}
              {planMetadata[currentOrganizationPlan.attributes.key]?.details && (
                <UnorderedList>
                  {(planMetadata[currentOrganizationPlan.attributes.key]?.details || []).map(
                    (detail, index) => (
                      <ListItem key={`current-plan-details-${index}`}>{detail}</ListItem>
                    ),
                  )}
                </UnorderedList>
              )}
            </PlanCard>
            <PlanCard isHighlighted={true}>
              <div>
                <Sticker variant='purple'>Recommended</Sticker>
              </div>
              <Spacer height={2} />
              <PlanCardIcon>
                {organizationPlanIconsMap[newOrganizationPlan['display-icon-key']]}
              </PlanCardIcon>
              <Spacer height={2} />
              <PlanCardTitle>{newOrganizationPlan['display-name']}</PlanCardTitle>
              {planMetadata[newOrganizationPlan.key]?.subtitle && (
                <>
                  <PlanCardSubtitle>
                    {planMetadata[newOrganizationPlan.key]?.subtitle}
                  </PlanCardSubtitle>
                  <Spacer height={3} />
                </>
              )}
              {planMetadata[newOrganizationPlan.key]?.details && (
                <UnorderedList>
                  {(planMetadata[newOrganizationPlan.key]?.details || []).map((detail, index) => (
                    <ListItem key={`new-plan-details-${index}`}>{detail}</ListItem>
                  ))}
                </UnorderedList>
              )}
            </PlanCard>
          </PlanCardsWrapper>
          <Spacer height={2} />
          <Footer>
            <a href='https://withpersona.com/pricing'>Compare all plans and features</a>
            <Button
              width='auto'
              onClick={() => {
                setNewOrganizationPlan(newOrganizationPlan);
                handleNavToNextView(View.ComparePlans, newOrganizationPlan);
              }}
            >
              Continue
            </Button>
          </Footer>
        </ModalBody>
      </StyledModalContainer>
    );
  }

  return (
    <StyledModalContainer>
      <ModalHeader
        prefixIcon={<ArrowLeftIcon size={20} />}
        onPrefixIconClick={() => handleNavToPreviousView(View.ComparePlans)}
        onClose={onClose}
      >
        Request access
      </ModalHeader>
      <ModalBody>
        <Paragraph size='lg' color='primary'>
          You’ll need to request access to this feature.
        </Paragraph>
        <Spacer height={2} />
        <StyledCard>
          Why don’t I already have access?
          <Spacer height={1} />
          <UnorderedList>
            <ListItem>Your current order form may not include this feature.</ListItem>
            <ListItem>
              Or, we’d like to perform a quick check to ensure that adding this feature to your
              implementation won’t cause any downstream issues. Once you submit your request, a
              member of the Persona team will reach out to you.
            </ListItem>
          </UnorderedList>
        </StyledCard>
        <Footer>
          <Button
            width='auto'
            onClick={() => {
              setNewOrganizationPlan(newOrganizationPlan);
              handleNavToNextView(View.ComparePlans, newOrganizationPlan);
            }}
          >
            Continue
          </Button>
        </Footer>
      </ModalBody>
    </StyledModalContainer>
  );
};

const StyledModalContainer = styled(ModalContainer)`
  min-width: 700px;
`;

const PlanCardsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  colunn-spacing: ${Spacing[4]};
`;

const PlanCard = styled(Card)<{ isHighlighted: boolean }>`
  flex: 1;
  min-width: 300px;
  padding: ${Spacing[4]} ${Spacing[5]};
  background-color: ${({ isHighlighted }) => isHighlighted && Color.CorePurple100};
`;

const PlanCardTitle = styled.div`
  ${Typography.HeadingMedium};
`;

const PlanCardSubtitle = styled.div`
  ${Typography.TextLarge};
`;

const PlanCardIcon = styled.div`
  display: flex;
  flex-direction: column;
  height: 100px;
  justify-content: center;
`;

const StyledCard = styled(Card)`
  padding: ${Spacing[3]} ${Spacing[4]};
  background-color: ${Color.CorePurple100};
  border-radius: ${Spacing[2]};
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[4]};
`;

const ListItem = styled('li')`
  margin-bottom: ${Spacing[2]};
`;

const Footer = styled.div`
  display: flex;
  flex-direction row;
  align-items: center;
  justify-content: end;
  column-gap: ${Spacing[4]};

  a {
    text-decoration: underline;
  }
`;

// Note: The available_plans endpoint only returns plans with relationship_status = 'customer'
// and plans that are equal or an "upgrade" to the current plan. Hence, only plans that are
// upgradeable have full details (they will be shown as the Recommended plan on the right.)
const planMetadata: Partial<
  Record<OrganizationPlanKey, { subtitle?: string | React.ReactNode; details?: string[] }>
> = {
  [OrganizationPlanKey.Starter]: {
    subtitle: 'One year of access for startups',
    details: ['Government ID & Selfie Verification', 'Liimted Verification configurations'],
  },
  [OrganizationPlanKey.StarterPenguin]: {
    subtitle: 'One year of access for startups',
    details: ['Government ID & Selfie Verification', 'Liimted Verification configurations'],
  },
  [OrganizationPlanKey.Essential]: {
    subtitle: (
      <>
        Starts at{' '}
        <Tooltip
          trigger='hover'
          placement='bottom'
          tooltip={
            <Paragraph color='primary' size='sm'>
              The Essential plan includes 500 free services per month (additional services will be
              charged $1.50 each).
            </Paragraph>
          }
          hideArrow={true}
        >
          <TextWithDashedUnderline>$250/month</TextWithDashedUnderline>
        </Tooltip>
      </>
    ),
    details: [
      'Access to a suite of industry-standard Verification & Report service types',
      'Platform features to automate business logic & complex processes',
      'Ability to configure Verification Checks and settings',
      'Unlock use of Add-On solutions for business verification and international KYC',
    ],
  },
  [OrganizationPlanKey.Growth]: {
    details: [
      'Advanced Verification & Report service types and configuration options including KYB',
      'Enhanced platform features to automate and conduct complex business processes',
      'Volume-based and cost-efficient pricing for your most used services',
      'Option to include guided implementation and personalized support options',
    ],
  },
};

export default ComparePlans;
